import React, { useState, useEffect } from 'react';
import notificationService from '../../services/notificationService';
import './NotificationPreferences.css';

const NotificationPreferences = () => {
    const [preferences, setPreferences] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testingNotification, setTestingNotification] = useState(false);
    const [permissionStatus, setPermissionStatus] = useState(null);

    useEffect(() => {
        loadPreferences();
        checkPermissionStatus();
    }, []);

    const loadPreferences = () => {
        try {
            const prefs = notificationService.getPreferences();
            setPreferences(prefs);
            setLoading(false);
        } catch (error) {
            console.error('Error loading preferences:', error);
            setLoading(false);
        }
    };

    const checkPermissionStatus = () => {
        const status = notificationService.getPermissionStatus();
        setPermissionStatus(status);
    };

    const handlePreferenceChange = (category, setting, value) => {
        const updatedPreferences = {
            ...preferences,
            categories: {
                ...preferences.categories,
                [category]: {
                    ...preferences.categories[category],
                    [setting]: value
                }
            }
        };
        setPreferences(updatedPreferences);
    };

    const handleGlobalSettingChange = (setting, value) => {
        const updatedPreferences = {
            ...preferences,
            [setting]: value
        };
        setPreferences(updatedPreferences);
    };

    const handleQuietHoursChange = (setting, value) => {
        const updatedPreferences = {
            ...preferences,
            quietHours: {
                ...preferences.quietHours,
                [setting]: value
            }
        };
        setPreferences(updatedPreferences);
    };

    const handleThresholdChange = (category, threshold, value) => {
        const updatedPreferences = {
            ...preferences,
            categories: {
                ...preferences.categories,
                [category]: {
                    ...preferences.categories[category],
                    thresholds: {
                        ...preferences.categories[category].thresholds,
                        [threshold]: parseFloat(value) || 0
                    }
                }
            }
        };
        setPreferences(updatedPreferences);
    };

    const savePreferences = async () => {
        setSaving(true);
        try {
            const success = notificationService.savePreferences(preferences);
            if (success) {
                // Also save to backend if user is authenticated
                // This would be implemented with an API call
                console.log('Preferences saved successfully');
            }
        } catch (error) {
            console.error('Error saving preferences:', error);
        } finally {
            setSaving(false);
        }
    };

    const requestPermission = async () => {
        const granted = await notificationService.requestPermission();
        checkPermissionStatus();
        return granted;
    };

    const testNotification = async (type = 'basic') => {
        setTestingNotification(true);
        try {
            if (!permissionStatus?.canSend) {
                const granted = await requestPermission();
                if (!granted) {
                    alert('Notification permission is required to test notifications');
                    return;
                }
            }

            const success = notificationService.sendTestNotification(type);
            if (success) {
                console.log(`Test notification (${type}) sent successfully`);
            } else {
                console.error('Failed to send test notification');
            }
        } catch (error) {
            console.error('Error testing notification:', error);
        } finally {
            setTestingNotification(false);
        }
    };

    if (loading) {
        return (
            <div className="notification-preferences loading">
                <div className="loading-spinner"></div>
                <p>Loading notification preferences...</p>
            </div>
        );
    }

    if (!preferences) {
        return (
            <div className="notification-preferences error">
                <p>Error loading notification preferences</p>
                <button onClick={loadPreferences}>Retry</button>
            </div>
        );
    }

    return (
        <div className="notification-preferences">
            <div className="preferences-header">
                <h2>Notification Preferences</h2>
                <p>Customize how and when you receive notifications</p>
            </div>

            {/* Permission Status */}
            <div className="permission-section">
                <h3>Browser Notifications</h3>
                <div className="permission-status">
                    <div className={`status-indicator ${permissionStatus?.canSend ? 'enabled' : 'disabled'}`}>
                        {permissionStatus?.canSend ? '✅' : '❌'}
                    </div>
                    <div className="status-info">
                        <p>
                            <strong>Status:</strong> {permissionStatus?.permission || 'Unknown'}
                        </p>
                        <p>
                            <strong>Supported:</strong> {permissionStatus?.isSupported ? 'Yes' : 'No'}
                        </p>
                    </div>
                    {!permissionStatus?.canSend && (
                        <button 
                            className="enable-notifications-btn"
                            onClick={requestPermission}
                        >
                            Enable Notifications
                        </button>
                    )}
                </div>
            </div>

            {/* Global Settings */}
            <div className="global-settings">
                <h3>Global Settings</h3>
                
                <div className="setting-item">
                    <label className="setting-label">
                        <input
                            type="checkbox"
                            checked={preferences.enabled}
                            onChange={(e) => handleGlobalSettingChange('enabled', e.target.checked)}
                        />
                        <span>Enable all notifications</span>
                    </label>
                </div>

                <div className="setting-item">
                    <label className="setting-label">
                        <input
                            type="checkbox"
                            checked={preferences.email?.enabled || false}
                            onChange={(e) => handleGlobalSettingChange('email', {
                                ...preferences.email,
                                enabled: e.target.checked
                            })}
                        />
                        <span>Enable email notifications</span>
                    </label>
                </div>

                <div className="setting-item">
                    <label className="setting-label">
                        <input
                            type="checkbox"
                            checked={preferences.email?.criticalOnly || false}
                            onChange={(e) => handleGlobalSettingChange('email', {
                                ...preferences.email,
                                criticalOnly: e.target.checked
                            })}
                            disabled={!preferences.email?.enabled}
                        />
                        <span>Email only for critical notifications</span>
                    </label>
                </div>
            </div>

            {/* Quiet Hours */}
            <div className="quiet-hours-section">
                <h3>Quiet Hours</h3>
                
                <div className="setting-item">
                    <label className="setting-label">
                        <input
                            type="checkbox"
                            checked={preferences.quietHours.enabled}
                            onChange={(e) => handleQuietHoursChange('enabled', e.target.checked)}
                        />
                        <span>Enable quiet hours (only critical notifications)</span>
                    </label>
                </div>

                {preferences.quietHours.enabled && (
                    <div className="quiet-hours-config">
                        <div className="time-inputs">
                            <div className="time-input">
                                <label>Start Time:</label>
                                <input
                                    type="time"
                                    value={preferences.quietHours.start}
                                    onChange={(e) => handleQuietHoursChange('start', e.target.value)}
                                />
                            </div>
                            <div className="time-input">
                                <label>End Time:</label>
                                <input
                                    type="time"
                                    value={preferences.quietHours.end}
                                    onChange={(e) => handleQuietHoursChange('end', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Category Settings */}
            <div className="category-settings">
                <h3>Notification Categories</h3>

                {Object.entries(preferences.categories).map(([category, settings]) => (
                    <div key={category} className="category-section">
                        <h4>{category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h4>
                        
                        <div className="setting-item">
                            <label className="setting-label">
                                <input
                                    type="checkbox"
                                    checked={settings.enabled}
                                    onChange={(e) => handlePreferenceChange(category, 'enabled', e.target.checked)}
                                />
                                <span>Enable {category.replace(/_/g, ' ')} notifications</span>
                            </label>
                        </div>

                        {settings.enabled && (
                            <>
                                <div className="setting-item">
                                    <label className="setting-label">
                                        <span>Frequency:</span>
                                        <select
                                            value={settings.frequency}
                                            onChange={(e) => handlePreferenceChange(category, 'frequency', e.target.value)}
                                        >
                                            <option value="all">All notifications</option>
                                            <option value="important">Important only</option>
                                            <option value="none">None</option>
                                        </select>
                                    </label>
                                </div>

                                {/* Performance thresholds */}
                                {category === 'performance' && settings.thresholds && (
                                    <div className="thresholds-section">
                                        <h5>Alert Thresholds</h5>
                                        <div className="threshold-inputs">
                                            <div className="threshold-input">
                                                <label>Gain threshold (%):</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="100"
                                                    value={settings.thresholds.gain}
                                                    onChange={(e) => handleThresholdChange(category, 'gain', e.target.value)}
                                                />
                                            </div>
                                            <div className="threshold-input">
                                                <label>Loss threshold (%):</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="100"
                                                    value={settings.thresholds.loss}
                                                    onChange={(e) => handleThresholdChange(category, 'loss', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Pool activity thresholds */}
                                {category === 'pool_activity' && settings.largeTradeThreshold !== undefined && (
                                    <div className="thresholds-section">
                                        <h5>Large Trade Threshold</h5>
                                        <div className="threshold-input">
                                            <label>Minimum trade size (SOL):</label>
                                            <input
                                                type="number"
                                                min="0.1"
                                                step="0.1"
                                                value={settings.largeTradeThreshold}
                                                onChange={(e) => handlePreferenceChange(category, 'largeTradeThreshold', parseFloat(e.target.value))}
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ))}
            </div>

            {/* Test Notifications */}
            <div className="test-section">
                <h3>Test Notifications</h3>
                <p>Test different types of notifications to see how they appear</p>
                
                <div className="test-buttons">
                    <button 
                        onClick={() => testNotification('basic')}
                        disabled={testingNotification}
                        className="test-btn"
                    >
                        Test Basic
                    </button>
                    <button 
                        onClick={() => testNotification('investment')}
                        disabled={testingNotification}
                        className="test-btn"
                    >
                        Test Investment
                    </button>
                    <button 
                        onClick={() => testNotification('performance')}
                        disabled={testingNotification}
                        className="test-btn"
                    >
                        Test Performance
                    </button>
                    <button 
                        onClick={() => testNotification('pool_activity')}
                        disabled={testingNotification}
                        className="test-btn"
                    >
                        Test Pool Activity
                    </button>
                </div>
            </div>

            {/* Save Button */}
            <div className="save-section">
                <button 
                    onClick={savePreferences}
                    disabled={saving}
                    className="save-preferences-btn"
                >
                    {saving ? 'Saving...' : 'Save Preferences'}
                </button>
            </div>
        </div>
    );
};

export default NotificationPreferences;